import { ApplicationView } from '@aha-app/mvc';
import type Whiteboard from '@aha-app/whiteboard';
import { parseOperations, WhiteboardController } from '@aha-app/whiteboard';
import AssistedWhiteboard from 'components/Assistant/agents/WhiteboardAgent/AssistedWhiteboard';
import { loadWhiteboard } from 'javascripts/async_modules';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { type ContentRef } from './ContentRef';

let WhiteboardComponent: typeof Whiteboard | null = null;

const loadWhiteboardCode = async () => {
  WhiteboardComponent = await loadWhiteboard();
};

interface WhiteboardArtifactProps {
  contentType:
    | 'application/vnd.aha.whiteboardOps'
    | 'application/vnd.aha.diagram';
  body: string;
  contentRef?: React.Ref<ContentRef>;
  isStreaming?: boolean;
}

const WhiteboardArtifact: React.FC<WhiteboardArtifactProps> = ({
  contentType,
  body,
  contentRef,
  isStreaming,
}) => {
  const isDiagram = contentType === 'application/vnd.aha.diagram';

  const [loading, setLoading] = useState(true);
  const [assistedWhiteboard, setAssistedWhiteboard] =
    useState<AssistedWhiteboard | null>(null);

  useImperativeHandle(
    contentRef,
    () => ({
      getCopyableContent: () => {
        if (!assistedWhiteboard) return '';
        return JSON.stringify(assistedWhiteboard.getWhiteboardJSON());
      },
    }),
    [assistedWhiteboard]
  );

  useEffect(() => {
    void loadWhiteboardCode().then(() => {
      // wrap to avoid updating state here while rendering another component
      setTimeout(() => setLoading(false));
    });
  }, []);

  const processedLines = React.useRef(0);
  useEffect(() => {
    if (assistedWhiteboard && body) {
      if (isDiagram && processedLines.current === 0) {
        // Add the diagram as a single SmartObject
        // this won't be called until the diagram is complete, because it is not a streamable type
        // use processedLines to indicate we've added the diagram, so it doesn't get added again on subsequent renders
        void assistedWhiteboard.actionAddDiagram(body, { animate: false });
        processedLines.current = 1;
      } else {
        // stream operations onto the board
        const animate = processedLines.current !== 0;
        const result = parseOperations(
          body,
          processedLines.current,
          !isStreaming
        );
        processedLines.current = result.newProcessedLines;
        void assistedWhiteboard.actionProcessOperations(
          result.operations,
          'initial',
          animate
        );
      }
    }
  }, [isStreaming, isDiagram, assistedWhiteboard, body]);

  const initialData = useMemo(() => ({ version: 2, objects: {} }), []);
  const controllerRef = useCallback(controller => {
    // Create the assisted whiteboard when controller is ready
    if (controller instanceof WhiteboardController) {
      setAssistedWhiteboard(new AssistedWhiteboard(controller));
    }
  }, []);

  if (loading || !WhiteboardComponent) {
    return <div>Loading {isDiagram ? 'diagram' : 'whiteboard'}...</div>;
  }

  return (
    <div style={{ height: '100%' }}>
      <WhiteboardComponent
        readOnly
        allowAnnotate={false}
        allowUpdate={false}
        initialVersion={0}
        canPresent={false}
        sidepanelDisabled
        initialData={initialData}
        controllerRef={controllerRef}
      />
    </div>
  );
};

export default ApplicationView(WhiteboardArtifact);
